import React, { useEffect, useRef, useState, useCallback } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Search, Send, Paperclip, Image as ImageIcon, Video as VideoIcon, MessageCircle, Phone, User as UserIcon, CheckCheck, Check, Trash2, ArrowLeft, Ban, ShieldCheck } from "lucide-react";

const BG_PATTERN = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><g fill='%23ffffff' fill-opacity='0.05'><circle cx='10' cy='10' r='1'/><circle cx='30' cy='30' r='1'/><circle cx='50' cy='50' r='1'/></g></svg>";

export default function AdminChat() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const scrollerRef = useRef(null);
  const fileImgRef = useRef(null);
  const fileVidRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chat/threads");
      setThreads(data.threads || []);
    } catch {}
  }, []);

  const loadThread = useCallback(async (uid) => {
    try {
      const { data } = await api.get(`/admin/chat/threads/${uid}`);
      setMessages(data.messages || []);
      setSelectedUser(data.user);
      await api.post(`/admin/chat/threads/${uid}/mark-read`).catch(() => {});
      loadThreads();
    } catch {}
  }, [loadThreads]);

  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, 5000);
    return () => clearInterval(id);
  }, [loadThreads]);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    const id = setInterval(() => loadThread(selected), 4000);
    return () => clearInterval(id);
  }, [selected, loadThread]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const send = async (payload) => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/chat/threads/${selected}`, payload);
      setMessages(m => [...m, data]);
      setText("");
      loadThreads();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sendText = () => {
    const t = text.trim();
    if (!t) return;
    send({ text: t });
  };

  const uploadFile = async (file, file_type) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25MB");
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    send({ file_data_url: dataUrl, filename: file.name, file_type });
  };

  const filtered = threads.filter(t => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (t.user_name || "").toLowerCase().includes(s) || (t.user_mobile || "").toLowerCase().includes(s);
  });

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      {/* Full-screen chat on mobile — escapes AdminLayout padding for true WhatsApp feel */}
      <div className="admin-chat-wrap -mx-2 sm:-mx-4 md:-mx-6 lg:-mx-8 -my-3 md:-my-8">
        <div
          className="flex bg-white overflow-hidden border-0 md:border md:border-slate-200 md:rounded-2xl md:shadow-sm md:mx-6 md:my-8"
          style={{ height: "calc(100dvh - 3.5rem)" }}
          data-testid="admin-chat-container"
        >
        {/* Threads sidebar — hidden on mobile when a chat is selected */}
        <aside className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-slate-200 flex-col bg-[#f0f2f5]`}>
          <div className="p-3 bg-[#008069] text-white flex items-center gap-2">
            <a href="/admin" className="md:hidden -ml-1 p-1.5 rounded-full hover:bg-white/10 active:bg-white/20" aria-label="Back to Admin" data-testid="admin-chat-nav-back">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-lg leading-tight">Live Chat</div>
              <div className="text-[11px] text-white/80">{threads.length} conversations · {threads.reduce((s, t) => s + (t.unread || 0), 0)} unread</div>
            </div>
            {threads.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm(`SAARE ${threads.length} users ki chat permanently delete karni hai?\n\nYeh action undo nahi hoga.`)) return;
                  try {
                    const { data } = await api.delete("/admin/chat/threads");
                    toast.success(`${data.deleted} messages delete ho gayi`);
                    setSelected(null);
                    loadThreads();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Delete failed");
                  }
                }}
                data-testid="admin-delete-all-threads-btn"
                className="p-2 rounded-full bg-rose-600 hover:bg-rose-700 active:scale-95 transition shadow-md border border-white/30"
                title="Delete ALL chats"
                aria-label="Delete all chats"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="p-2 bg-[#f6f6f6] border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or mobile"
                className="w-full pl-9 pr-3 py-2 rounded-full bg-white text-sm border border-slate-200 outline-none focus:border-emerald-400" data-testid="thread-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="thread-list">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-slate-400 text-sm">
                <MessageCircle className="w-10 h-10 mx-auto opacity-30 mb-2" />
                Koi chat abhi tak nahi
              </div>
            )}
            {filtered.map(t => (
              <ThreadRow key={t.user_id} thread={t} active={selected === t.user_id} onClick={() => setSelected(t.user_id)} />
            ))}
          </div>
        </aside>

        {/* Chat panel — hidden on mobile until selection */}
        <main className={`${selected ? "flex" : "hidden md:flex"} flex-1 flex-col bg-[#efeae2] min-w-0`} style={{ backgroundImage: `url('${BG_PATTERN}')`, backgroundRepeat: "repeat" }}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto opacity-30 mb-3" />
                <div className="text-lg font-semibold text-slate-600">Select a chat</div>
                <div className="text-sm text-slate-400 mt-1">Left sidebar se koi user select karo</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="shrink-0 bg-[#008069] text-white flex items-center gap-2 px-3 md:px-4 py-3">
                <button
                  onClick={() => setSelected(null)}
                  className="md:hidden p-1.5 rounded-full hover:bg-white/10 active:bg-white/20"
                  aria-label="Back"
                  data-testid="admin-chat-back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden shrink-0">
                  <UserIcon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5" data-testid="chat-user-name">
                    {selectedUser?.name || "User"}
                    {selectedUser?.chat_banned && (
                      <span data-testid="chat-banned-badge" className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/90 text-white tracking-wider">
                        <Ban className="w-2.5 h-2.5" /> BANNED
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/80 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    <span data-testid="chat-user-mobile">{selectedUser?.mobile || ""}</span>
                  </div>
                </div>
                <button
                  data-testid="admin-ban-chat-btn"
                  onClick={async () => {
                    const willBan = !selectedUser?.chat_banned;
                    const msg = willBan
                      ? `${selectedUser?.name || 'User'} ki chat BAN karni hai? Vo message nahi bhej payega par app me login rahega.`
                      : `${selectedUser?.name || 'User'} ki chat UNBAN karni hai? Vo wapas message bhej payega.`;
                    if (!window.confirm(msg)) return;
                    try {
                      const { data } = await api.post(`/admin/chat/threads/${selected}/ban-toggle`);
                      setSelectedUser(su => su ? { ...su, chat_banned: data.chat_banned } : su);
                      toast.success(data.chat_banned ? "User chat BAN kar diya" : "User chat UNBAN kar diya");
                      loadThreads();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className={`p-2 rounded-full hover:bg-white/10 active:bg-white/20 ${selectedUser?.chat_banned ? 'text-emerald-200' : 'text-white'}`}
                  title={selectedUser?.chat_banned ? "Unban chat (user wapas message bhej payega)" : "Ban chat (user message nahi bhej payega)"}
                >
                  {selectedUser?.chat_banned ? <ShieldCheck className="w-5 h-5" /> : <Ban className="w-5 h-5" />}
                </button>
                <button
                  data-testid="admin-delete-thread-btn"
                  onClick={async () => {
                    if (!window.confirm(`Puri chat delete karni hai ${selectedUser?.name || 'user'} ki? Yeh permanent hai.`)) return;
                    try {
                      await api.delete(`/admin/chat/threads/${selected}`);
                      setMessages([]);
                      toast.success("Thread deleted");
                      loadThreads();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="p-2 rounded-full hover:bg-white/10 active:bg-white/20"
                  title="Delete this chat"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1" data-testid="admin-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-slate-500 text-sm my-6">Koi message nahi hai — pehli reply bhejo</div>
                )}
                {messages.map((m, i) => <ChatBubble key={m.id} msg={m} prev={messages[i - 1]} />)}
              </div>

              {/* Input */}
              <div className="shrink-0 bg-[#f0f2f5] px-3 py-2 flex items-end gap-2">
                <button onClick={() => fileImgRef.current?.click()} className="w-10 h-10 rounded-full hover:bg-slate-200 text-slate-500 flex items-center justify-center" data-testid="admin-attach-img"><ImageIcon className="w-5 h-5" /></button>
                <button onClick={() => fileVidRef.current?.click()} className="w-10 h-10 rounded-full hover:bg-slate-200 text-slate-500 flex items-center justify-center" data-testid="admin-attach-vid"><VideoIcon className="w-5 h-5" /></button>
                <input ref={fileImgRef} type="file" accept="image/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "image")} />
                <input ref={fileVidRef} type="file" accept="video/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "video")} />

                <div className="flex-1 bg-white rounded-3xl px-4 min-h-[42px] flex items-center">
                  <textarea value={text} onChange={e => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                    rows={1} placeholder="Type a message" className="flex-1 resize-none outline-none py-2 text-sm max-h-32" data-testid="admin-chat-input" />
                </div>
                <button onClick={sendText} disabled={sending || !text.trim()} data-testid="admin-chat-send"
                  className="w-11 h-11 rounded-full bg-[#00a884] hover:bg-[#00c9a0] text-white flex items-center justify-center shadow-lg disabled:opacity-40">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </main>
        </div>
      </div>
    </AdminLayout>
  );
}

function ThreadRow({ thread, active, onClick }) {
  const lm = thread.last_message;
  const preview = lm?.text || (lm?.file_type ? `📎 ${lm.file_type}` : "");
  const t = lm?.created_at ? new Date(lm.created_at) : null;
  return (
    <button onClick={onClick} data-testid={`thread-${thread.user_id}`}
      className={`w-full flex items-center gap-3 px-3 py-3 border-b border-slate-100 text-left transition ${active ? "bg-emerald-50" : "hover:bg-white"}`}>
      <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold uppercase shrink-0">
        {(thread.user_name || "?").charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
            {thread.user_name || "Unknown"}
            {thread.chat_banned && (
              <span data-testid={`thread-ban-${thread.user_id}`} className="inline-flex items-center text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-500 text-white tracking-wider shrink-0">
                BAN
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 shrink-0 ml-2">
            {t ? (isToday(t) ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : t.toLocaleDateString([], { day: "numeric", month: "short" })) : ""}
          </div>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <div className="text-xs text-slate-500 truncate max-w-[180px]">
            {lm?.sender === "admin" ? <span className="text-slate-400">You: </span> : null}
            {preview}
          </div>
          {thread.unread > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">
              {thread.unread}
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">{thread.user_mobile}</div>
      </div>
    </button>
  );
}

function isToday(d) {
  const now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function ChatBubble({ msg, prev }) {
  const isAdmin = msg.sender === "admin";
  const t = new Date(msg.created_at);
  const timeLabel = t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const showDateSep = !prev || new Date(prev.created_at).toDateString() !== t.toDateString();
  return (
    <>
      {showDateSep && (
        <div className="flex justify-center my-2">
          <div className="bg-white/80 text-slate-500 text-[11px] px-3 py-1 rounded-md shadow-sm">
            {t.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
      )}
      <div className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
        <div className={`relative max-w-[70%] px-2.5 py-1.5 rounded-lg text-sm shadow-sm ${isAdmin ? "bg-[#d9fdd3] text-slate-900" : "bg-white text-slate-900"}`}>
          {msg.file_url && <div className="mb-1 -mx-1.5 -mt-1"><Attachment msg={msg} /></div>}
          {msg.text && <div className="whitespace-pre-wrap break-words pr-14 leading-snug">{msg.text}</div>}
          <div className="absolute bottom-1 right-2 flex items-center gap-1 text-[10px] text-slate-500">
            <span>{timeLabel}</span>
            {isAdmin && (msg.read_by_user ? <CheckCheck className="w-3.5 h-3.5 text-sky-500" /> : <Check className="w-3.5 h-3.5" />)}
          </div>
          <div className="h-4" />
        </div>
      </div>
    </>
  );
}

function Attachment({ msg }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const url = msg.file_url?.startsWith("http") ? msg.file_url : `${backend}${msg.file_url}`;
  if (msg.file_type === "image") return <img src={url} alt="" className="max-w-full rounded-md" style={{ maxHeight: 260 }} />;
  if (msg.file_type === "video") return <video src={url} controls className="max-w-full rounded-md" style={{ maxHeight: 260 }} />;
  if (msg.file_type === "audio") return <audio src={url} controls className="w-64 max-w-full" />;
  return <a href={url} target="_blank" rel="noreferrer" className="underline text-emerald-700">Open file</a>;
}
